/**
 * Agent Commission Calculator Component
 * Calculates potential earnings based on net fee (after scholarship deduction)
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { commissionCalculatorAPI, universitiesAPI } from '@/lib/api';
import { 
  Calculator, DollarSign, GraduationCap, Building, 
  AlertTriangle, TrendingUp, Percent, Info
} from 'lucide-react';

export const CommissionCalculator = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    university_id: '',
    gross_tuition_fee: '',
    scholarship_amount: '0',
    currency: 'GBP'
  });

  // Currency symbols
  const currencySymbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    CAD: 'C$'
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    setLoading(true);
    try {
      const response = await universitiesAPI.getAll();
      // Filter universities that have commission set
      const unisWithCommission = response.data.filter(u => u.commission_percentage);
      setUniversities(unisWithCommission);
    } catch (error) {
      toast.error('Failed to load universities');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.university_id) {
      toast.error('Please select a university');
      return;
    }
    if (!formData.gross_tuition_fee || parseFloat(formData.gross_tuition_fee) <= 0) {
      toast.error('Please enter a valid tuition fee');
      return;
    }

    setCalculating(true);
    try {
      const response = await commissionCalculatorAPI.calculate({
        university_id: formData.university_id,
        gross_tuition_fee: parseFloat(formData.gross_tuition_fee),
        scholarship_amount: parseFloat(formData.scholarship_amount) || 0
      });
      setResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate commission');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${currencySymbols[formData.currency]}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const selectedUniversity = universities.find(u => u.id === formData.university_id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Commission Calculator</h2>
        </div>
        <p className="text-teal-100">
          Calculate your potential earnings from student placements
        </p>
      </div>

      {/* Important Disclaimer */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-800 dark:text-amber-200 text-lg mb-2">
              IMPORTANT: Commission Terms
            </h3>
            <p className="text-amber-700 dark:text-amber-300">
              <strong className="text-amber-900 dark:text-amber-100">
                Commission is payable on NET FEE ONLY, after deducting scholarships.
              </strong>
            </p>
            <p className="text-amber-700 dark:text-amber-300 mt-2">
              <strong className="text-amber-900 dark:text-amber-100">
                Taxes and fees apply at the time of raising an invoice.
              </strong>
            </p>
          </div>
        </div>
      </div>

      {/* Calculator Form */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-teal-600" />
          Enter Details
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* University Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Select University
            </Label>
            <Select
              value={formData.university_id}
              onValueChange={(val) => setFormData({ ...formData, university_id: val })}
            >
              <SelectTrigger data-testid="calculator-university-select">
                <SelectValue placeholder={loading ? "Loading..." : "Choose a university"} />
              </SelectTrigger>
              <SelectContent>
                {universities.map(uni => (
                  <SelectItem key={uni.id} value={uni.id}>
                    {uni.name} ({uni.commission_percentage}% comm)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedUniversity && (
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                  {selectedUniversity.commission_percentage}% University Commission
                </Badge>
                <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                  {selectedUniversity.agent_share_percentage || 70}% Your Share
                </Badge>
              </div>
            )}
          </div>

          {/* Currency Selection */}
          <div className="space-y-2">
            <Label>Currency</Label>
            <Select
              value={formData.currency}
              onValueChange={(val) => setFormData({ ...formData, currency: val })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GBP">GBP (£) - British Pound</SelectItem>
                <SelectItem value="USD">USD ($) - US Dollar</SelectItem>
                <SelectItem value="EUR">EUR (€) - Euro</SelectItem>
                <SelectItem value="CAD">CAD (C$) - Canadian Dollar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Gross Tuition Fee */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Gross Tuition Fee
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                {currencySymbols[formData.currency]}
              </span>
              <Input
                type="number"
                value={formData.gross_tuition_fee}
                onChange={(e) => setFormData({ ...formData, gross_tuition_fee: e.target.value })}
                placeholder="e.g., 15000"
                className="pl-8"
                data-testid="calculator-gross-fee"
              />
            </div>
            <p className="text-xs text-gray-500">Total tuition fee before any deductions</p>
          </div>

          {/* Scholarship Amount */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Scholarship Amount
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                {currencySymbols[formData.currency]}
              </span>
              <Input
                type="number"
                value={formData.scholarship_amount}
                onChange={(e) => setFormData({ ...formData, scholarship_amount: e.target.value })}
                placeholder="e.g., 2000"
                className="pl-8"
                data-testid="calculator-scholarship"
              />
            </div>
            <p className="text-xs text-gray-500">Scholarship or discount amount to deduct</p>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          disabled={calculating || !formData.university_id || !formData.gross_tuition_fee}
          className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3"
          data-testid="calculate-commission-btn"
        >
          {calculating ? (
            <>Calculating...</>
          ) : (
            <>
              <Calculator className="w-5 h-5 mr-2" />
              Calculate My Commission
            </>
          )}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Percent className="w-6 h-6" />
              Commission Breakdown
            </h3>
            {result.university_name && (
              <p className="text-teal-100 mt-1">{result.university_name}</p>
            )}
          </div>

          <div className="p-6 space-y-4">
            {/* Fee Breakdown */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Gross Tuition Fee</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">
                  {formatCurrency(result.gross_tuition_fee)}
                </p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <p className="text-sm text-red-600 dark:text-red-400">Scholarship Deduction</p>
                <p className="text-xl font-bold text-red-700 dark:text-red-300">
                  - {formatCurrency(result.scholarship_amount)}
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">NET FEE (Commission Base)</p>
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {formatCurrency(result.net_fee)}
                </p>
              </div>
            </div>

            {/* Commission Split */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
                Commission Split ({result.commission_percentage}% of Net Fee)
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Commission</p>
                  <p className="text-xl font-bold text-gray-800 dark:text-white">
                    {formatCurrency(result.total_commission)}
                  </p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 border-2 border-teal-300 dark:border-teal-700">
                  <p className="text-sm text-teal-600 dark:text-teal-400 font-medium">
                    Your Share ({result.agent_share_percentage}%)
                  </p>
                  <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                    {formatCurrency(result.agent_commission)}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    H E Direct ({result.he_direct_share_percentage}%)
                  </p>
                  <p className="text-xl font-bold text-orange-700 dark:text-orange-300">
                    {formatCurrency(result.he_direct_commission)}
                  </p>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Note:</strong> {result.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-500" />
          How Commission Works
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold">1.</span>
            <span>Universities pay H E Direct a commission percentage when a student enrolls.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold">2.</span>
            <span>Commission is calculated on the <strong>NET FEE</strong> (gross fee minus scholarships).</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold">3.</span>
            <span>The standard split is <strong>70% to Agent, 30% to H E Direct</strong>.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600 font-bold">4.</span>
            <span>Actual commission is paid after the student completes enrollment.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CommissionCalculator;
